import React, { useState } from 'react'
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  CircularProgress,
} from '@mui/material'
import {
  FileDownload as FileDownloadIcon,
  Description as DescriptionIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import { useDataApi } from '../../context/DataApiContext'
import { useNotificationsProvider } from '../../context/NotificationsContext'
import { Roles } from '../../entities/permissions'
import AutoCompleteInput from '../AutoCompleteInput'
import BaseDialog from '../BaseDialog'

import ChassisTemplate from '../Chassis/components/CreateChassisBulkDialog/steps/ImportFileStep/chassis-template.xlsx'
import VehicleTypeItemsTemplate from '../VehicleType/components/CreateVehicleTypeBulkDialog/steps/ImportFileStep/type-vehicle-items-template.xlsx'
import PVAdmissionTemplate from '../PVAdmissionManagement/steps/ImportFileStep/pv-admission-template.docx'

/**
 * Centralised import templates dropdown menu.
 *
 * Renders a button in the app header that lists all available import
 * templates.  Static templates (chassis, vehicle-type items, PV admission)
 * are downloaded directly.  The controls-calendar template requires the
 * user to pick a constructor first (via a small dialog).
 *
 * Visible only to admin, super-admin and CATP users.
 */

const STATIC_TEMPLATES = [
  {
    key: 'chassis',
    labelKey: 'import-templates.chassis',
    href: ChassisTemplate,
    filename: 'chassis-template.xlsx',
  },
  {
    key: 'vehicle-type-items',
    labelKey: 'import-templates.vehicle-type-items',
    href: VehicleTypeItemsTemplate,
    filename: 'type-vehicle-items-template.xlsx',
  },
  {
    key: 'pv-admission',
    labelKey: 'import-templates.pv-admission',
    href: PVAdmissionTemplate,
    filename: 'pv-admission-template.docx',
  },
]

const ALLOWED_ROLES = ['admin', 'super-admin', Roles.CATP]

export function canAccessImportTemplates(user) {
  return user && ALLOWED_ROLES.includes(user.role)
}

export default function ImportTemplatesMenu() {
  const { t } = useTranslation()
  const { dataProvider } = useDataApi()
  const { showNotification } = useNotificationsProvider()

  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  // Calendar template dialog state
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false)
  const [calendarConstructor, setCalendarConstructor] = useState(null)
  const [calendarLoading, setCalendarLoading] = useState(false)

  const handleOpen = (event) => setAnchorEl(event.currentTarget)
  const handleClose = () => setAnchorEl(null)

  const handleStaticDownload = (template) => {
    const link = document.createElement('a')
    link.href = template.href
    link.setAttribute('download', template.filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    handleClose()
  }

  const handleCalendarClick = () => {
    handleClose()
    setCalendarConstructor(null)
    setCalendarDialogOpen(true)
  }

  const handleCalendarDownload = async () => {
    if (!calendarConstructor) return
    setCalendarLoading(true)
    try {
      await dataProvider.calendar.exportTemplate(calendarConstructor.id)
      setCalendarDialogOpen(false)
    } catch (err) {
      console.error(
        '[ImportTemplatesMenu] calendar template download failed',
        err
      )
      showNotification('error', t('import-templates.calendar-error'))
    } finally {
      setCalendarLoading(false)
    }
  }

  return (
    <>
      <Button
        color="primary"
        startIcon={<FileDownloadIcon />}
        endIcon={<ExpandMoreIcon />}
        onClick={handleOpen}
        aria-controls={open ? 'import-templates-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        sx={{ textTransform: 'none', fontWeight: 400, mx: 1 }}
      >
        {t('import-templates.menu-label')}
      </Button>

      <Menu
        id="import-templates-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {STATIC_TEMPLATES.map((template) => (
          <MenuItem
            key={template.key}
            onClick={() => handleStaticDownload(template)}
          >
            <ListItemIcon>
              <DescriptionIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t(template.labelKey)}</ListItemText>
          </MenuItem>
        ))}

        <Divider />

        <MenuItem onClick={handleCalendarClick}>
          <ListItemIcon>
            <DescriptionIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('import-templates.controls-calendar')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Constructor selection dialog for calendar template */}
      <BaseDialog
        open={calendarDialogOpen}
        onClose={() => {
          setCalendarDialogOpen(false)
        }}
        title={t('import-templates.controls-calendar')}
        width="xs"
        hideActions
      >
        <Box sx={{ pt: 1 }}>
          <AutoCompleteInput
            onChange={setCalendarConstructor}
            label={t('import-templates.select-constructor')}
            resource="constructors/autocomplete"
            value={calendarConstructor}
            canDelete
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            disabled={!calendarConstructor || calendarLoading}
            onClick={handleCalendarDownload}
            startIcon={
              calendarLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {t('common.download')}
          </Button>
        </Box>
      </BaseDialog>
    </>
  )
}
